import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Events:
 * - "comparison:focusPathway"  detail: { pathway: string }
 */
function emitFocusPathway(pathway) {
  window.dispatchEvent(
    new CustomEvent("comparison:focusPathway", { detail: { pathway } }),
  );
}

export default function PathwayMultiSelect({
  pathways = [],
  selected = [],
  setSelected,
  getColorByName = () => "#80D5FF",
  max = 5,
  placeholder = "Select Pathways",
  disabled = false,
  className = "",

  showSelectedPanel = false,
  selectedTitle = "Selected pathways",
  emptySelectedText = "No pathways selected yet.",
  clearText = "Clear selection",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  // drag state
  const dragIndexRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = Array.isArray(pathways) ? pathways : [];
    if (!q) return base;
    return base.filter((p) => String(p).toLowerCase().includes(q));
  }, [pathways, query]);

  const selectedCount = selected.length;

  const toggle = (p) => {
    setSelected((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (cur.includes(p)) return cur.filter((x) => x !== p);
      if (cur.length >= max) return cur;
      return [...cur, p];
    });
  };

  const remove = (p) => setSelected((prev) => prev.filter((x) => x !== p));
  const clearAll = () => setSelected([]);

  const reorder = (fromIdx, toIdx) => {
    if (fromIdx == null || toIdx == null) return;
    if (fromIdx === toIdx) return;

    setSelected((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  };

  const Trigger = (
    <div className={`relative z-[120] w-full`} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onPointerDown={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`
          w-full relative
          rounded-xl border border-slate-200
          px-3 py-2 pr-10
          text-left text-[14px] text-slate-900
          shadow-sm outline-none
          ${open || selectedCount > 0 ? "bg-white" : "bg-slate-50"}
          ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-slate-300 active:bg-white"}
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {/* ✅ NO mostramos chips aquí */}
        {selectedCount === 0 ? (
          <span className="text-[#A7A7A7]">{placeholder}</span>
        ) : (
          <span className="text-[#101010]">
            {selectedCount}/{max} selected
          </span>
        )}

        <span
          className={`
            pointer-events-none
            absolute right-3 top-1/2 -translate-y-1/2
            w-2 h-2 border border-[#686873] border-t-0 border-l-0 rotate-45
            transition-transform
            ${open ? "rotate-[225deg] translate-y-[-10%]" : ""}
          `}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="
            absolute left-0 mt-2 w-full bg-white
            border border-[#F2F2F2] rounded-lg shadow-xl z-[130]
            overflow-hidden
          "
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#F2F2F2] bg-white">
            <div className="text-[12px] text-[#7E8081]">
              {selectedCount}/{max} selected
            </div>

            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearAll();
              }}
              className="text-[12px] text-[#7E8081] hover:text-[#101010]"
              disabled={selectedCount === 0}
            >
              Clear
            </button>
          </div>

          <div className="px-3 py-2 border-b border-[#F2F2F2] bg-white">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="
                w-full rounded-lg border border-slate-200 bg-slate-50
                px-3 py-2 text-[13px] text-[#101010]
                outline-none focus:border-slate-300
              "
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {options.length === 0 && (
              <div className="px-4 py-3 text-[14px] text-[#98999A]">
                No options available
              </div>
            )}

            {options.map((p) => {
              const picked = selected.includes(p);
              const locked = !picked && selectedCount >= max;

              return (
                <button
                  key={p}
                  type="button"
                  role="option"
                  aria-selected={picked}
                  disabled={locked}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (locked) return;
                    toggle(p);
                  }}
                  className={`
                    w-full flex items-center justify-between
                    px-4 py-2 text-[14px]
                    ${picked ? "bg-[#F0F5F0] text-[#0F0F0F]" : "text-[#101010]"}
                    ${locked ? "opacity-50 cursor-not-allowed" : "hover:bg-[#F7F9FA]"}
                  `}
                  title={p}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getColorByName(p) }}
                    />
                    <span className="truncate">{p}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    {picked ? (
                      <span className="text-[#54B046] text-[14px]">✓</span>
                    ) : locked ? (
                      <span className="text-[12px] text-[#7E8081]">Max</span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t border-[#F2F2F2] bg-white flex justify-end">
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              }}
              className="text-[13px] text-[#101010] hover:opacity-80"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (!showSelectedPanel) {
    return <div className={`w-full ${className}`}>{Trigger}</div>;
  }

  return (
    <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8 ${className}`}>
      {/* left */}
      <div className="min-w-[320px]">
        {Trigger}
        <div className="mt-2 text-[12px] text-[#7E8081]">
          {selectedCount}/{max} selected
        </div>
      </div>

      {/* right */}
      <div className="md:pt-1">
        <div className="text-sm font-semibold text-[#101010] mb-2">
          {selectedTitle}
        </div>

        {selectedCount === 0 ? (
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200 text-sm text-slate-700">
            {emptySelectedText}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selected.map((p, idx) => (
              <div
                key={p}
                draggable
                onDragStart={(e) => {
                  dragIndexRef.current = idx;
                  e.dataTransfer.effectAllowed = "move";
                  try {
                    e.dataTransfer.setData("text/plain", String(idx));
                  } catch {}
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from =
                    dragIndexRef.current ??
                    Number(e.dataTransfer.getData("text/plain"));
                  const to = idx;
                  dragIndexRef.current = null;
                  reorder(from, to);
                }}
                onClick={() => emitFocusPathway(p)}
                className="
                  cursor-pointer select-none
                  flex items-center gap-2 rounded-full border border-[#E3E5E6]
                  bg-white px-3 py-2 shadow-sm
                  hover:bg-[#F7F9FA]
                "
                title="Click to focus in charts · Drag to reorder"
              >
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: getColorByName(p) }}
                />
                <span className="text-[13px] text-[#101010] max-w-[260px] truncate">
                  {p}
                </span>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(p);
                  }}
                  className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[#7E8081] hover:bg-white hover:text-[#101010]"
                  aria-label={`Remove ${p}`}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {selectedCount > 0 && (
          <div className="mt-3 flex items-center gap-4">
            <button
              type="button"
              onClick={clearAll}
              className="text-[13px] text-[#7E8081] hover:text-[#101010]"
            >
              {clearText}
            </button>

            <span className="text-[12px] text-[#98999A]">
              Tip: drag chips to reorder · click to focus legend
            </span>
          </div>
        )}
      </div>
    </div>
  );
}